import { useCallback, useEffect, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Loader2, ZoomIn } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

interface ImageCropDialogProps {
  /** The originally-selected file to crop. Null when the dialog is closed. */
  file: File | null;
  /** Crop aspect ratio (width / height). Defaults to 1 (square, the listing cover shape). */
  aspect?: number;
  /** Output edge length in px for the longest side. Defaults to 1024. */
  outputSize?: number;
  onCancel: () => void;
  /** Receives the cropped + resized file, ready for moderation + upload. */
  onCropComplete: (croppedFile: File) => void;
}

/**
 * Draws the selected crop region onto a canvas resized to a standard size and
 * re-encodes it as a JPEG. This both frames the image to the listing's square
 * cover shape and shrinks it (fixing >5MB rejections), replacing the old blind
 * CDN auto-crop with an operator-controlled crop.
 */
async function getCroppedFile(
  src: string,
  cropPixels: Area,
  outputSize: number,
  fileName: string,
): Promise<File> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  // Scale the output so the longest cropped edge maps to outputSize, but never
  // upscale beyond the source crop.
  const longest = Math.max(cropPixels.width, cropPixels.height);
  const scale = Math.min(1, outputSize / longest);
  const targetW = Math.round(cropPixels.width * scale);
  const targetH = Math.round(cropPixels.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    targetW,
    targetH,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9),
  );
  if (!blob) throw new Error("Failed to encode cropped image");

  const baseName = fileName.replace(/\.[^./]+$/, "") || "image";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

export function ImageCropDialog({
  file,
  aspect = 1,
  outputSize = 1024,
  onCancel,
  onCropComplete,
}: ImageCropDialogProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  // Build (and revoke) an object URL for the selected file.
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropPixels = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !file) return;
    setProcessing(true);
    try {
      const cropped = await getCroppedFile(
        imageSrc,
        croppedAreaPixels,
        outputSize,
        file.name,
      );
      onCropComplete(cropped);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Frame your photo</DialogTitle>
          <DialogDescription>
            Drag to reposition and zoom so the photo fills the square. This is
            how it will appear on your storefront.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-muted">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropPixels}
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-1">
          <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0])}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={processing}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={processing || !croppedAreaPixels}>
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing…
              </>
            ) : (
              "Use photo"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
